import { CSSProperties, Fragment, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { formatHoursToTimeString } from '@/helpers';
import { cn } from '@/lib/utils';

import { buildBarSegments, CapLineZone, getTrackedStateColor } from './unified-summary.helpers';

interface UnifiedSummaryProps {
  /** Top-row eyebrow label, e.g. "This week", "Monday, May 13". */
  label: string;
  /** Optional secondary label (typically a date range). */
  sublabel?: string;
  /** Hours actually tracked in the period. */
  tracked: number;
  /** Subset of `tracked` that was logged on background-work assignments. */
  trackedBackground?: number;
  /** Hours booked (assigned) in the period. */
  booked: number;
  /** Effective working capacity for the period — raw capacity minus APPROVED time-off. */
  effectiveCapacity: number;
  /** Approved time-off hours in the period. Defaults to 0. */
  timeOffApproved?: number;
  /** Pending time-off hours in the period. Defaults to 0. */
  timeOffPending?: number;
  /** Compact rendering (smaller text, tighter padding) for embedded contexts. */
  variant?: UnifiedSummaryVariant;
  /** Remove the outer card chrome (border + bg + shadow) so this can embed in a parent card. */
  bare?: boolean;
}

/** Rendering density. */
export enum UnifiedSummaryVariant {
  Default = 'default',
  Compact = 'compact',
}

/** Legend item identifiers — drive both rendering order and hover linkage. */
enum LegendKey {
  TimeOffApproved = 'timeoff-approved',
  TimeOffPending = 'timeoff-pending',
  Tracked = 'tracked',
  TrackedBackground = 'tracked-background',
  Todo = 'todo',
  Available = 'available',
  Over = 'over',
}

/** Bar segment identifiers — one per visually distinct region of the bar. */
enum SegmentKey {
  TimeOffApproved = 'timeoff-approved',
  TimeOffPending = 'timeoff-pending',
  TrackedRegularUnder = 'tracked-regular-under',
  TrackedBackgroundUnder = 'tracked-background-under',
  TodoUnder = 'todo-under',
  Available = 'available',
  TrackedRegularOver = 'tracked-regular-over',
  TrackedBackgroundOver = 'tracked-background-over',
  TodoOver = 'todo-over',
}

/** Which bar segments should highlight when a given legend item is hovered. */
const LEGEND_TO_SEGMENTS: Record<LegendKey, SegmentKey[]> = {
  [LegendKey.TimeOffApproved]: [SegmentKey.TimeOffApproved],
  [LegendKey.TimeOffPending]: [SegmentKey.TimeOffPending],
  [LegendKey.Tracked]: [SegmentKey.TrackedRegularUnder, SegmentKey.TrackedRegularOver],
  [LegendKey.TrackedBackground]: [
    SegmentKey.TrackedBackgroundUnder,
    SegmentKey.TrackedBackgroundOver,
  ],
  [LegendKey.Todo]: [SegmentKey.TodoUnder, SegmentKey.TodoOver],
  [LegendKey.Available]: [SegmentKey.Available],
  [LegendKey.Over]: [
    SegmentKey.TrackedRegularOver,
    SegmentKey.TrackedBackgroundOver,
    SegmentKey.TodoOver,
  ],
};

/** Bar dimensions (px). */
const BAR_HEIGHT_DEFAULT_PX = 20;
const BAR_HEIGHT_COMPACT_PX = 14;
/** Vertical space reserved above the bar for the "Xh cap" chip when shown. */
const CAP_CHIP_OFFSET_PX = 18;
/** Cap divider extends slightly beyond the bar top/bottom for visibility. */
const CAP_DIVIDER_OVERHANG_PX = 3;
/** Opacity applied to background-work segments so they read as muted. */
const BACKGROUND_WORK_OPACITY = 0.55;

/**
 * Theme-aware bar colors.
 *
 * `TIMEOFF_*` and `AVAILABLE` resolve through CSS variables so they swap between
 * light + dark mode. `OVER` is the brand overflow signal; the same purple in
 * both themes so over-capacity always reads the same way.
 */
const SUMMARY_COLOR_TIMEOFF = 'hsl(var(--time-off))';
const SUMMARY_COLOR_AVAILABLE = 'hsl(var(--track-empty))';
const SUMMARY_COLOR_OVER = '#9333EA'; // purple-600

// Striped background gradient used for "left to track" / pending-time-off / overflow
// segments. The gap color resolves to the unfilled track token so the pattern
// reads correctly on both light and dark themes (no hardcoded white).
const stripedBg = (color: string) =>
  `repeating-linear-gradient(45deg, ${color} 0 5px, hsl(var(--track-empty)) 5px 10px)`;

const swatchStripedBg = (color: string) =>
  `repeating-linear-gradient(45deg, ${color} 0 3px, hsl(var(--track-empty)) 3px 6px)`;

interface LegendItem {
  key: LegendKey;
  label: string;
  hours: number;
  swatch: CSSProperties;
  /** Whether to color the text in the "over capacity" purple. */
  isOver?: boolean;
  /** Whether to render with a leading "+". */
  isPlus?: boolean;
  /** Tooltip text. */
  explain: string;
}

export function UnifiedSummary({
  label,
  sublabel,
  tracked,
  trackedBackground = 0,
  booked,
  effectiveCapacity,
  timeOffApproved = 0,
  timeOffPending = 0,
  variant = UnifiedSummaryVariant.Default,
  bare = false,
}: UnifiedSummaryProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  const isCompact = variant === UnifiedSummaryVariant.Compact;

  const segments = buildBarSegments({
    tracked,
    trackedBackground,
    booked,
    effectiveCapacity: effectiveCapacity,
    timeOffApproved,
    timeOffPending,
  });

  const trackedColor = getTrackedStateColor(tracked, Math.max(0, effectiveCapacity));

  const todo = Math.max(0, booked - tracked);
  const todoTotal = segments.todoUnder + segments.todoOver;
  const trackedBackgroundTotal = segments.trackedBackgroundUnder + segments.trackedBackgroundOver;

  // time-off and work zones are visually separate tracks (with a small gap), like in the
  // original design. Each gets its own rounded background.
  const timeOffTotal = segments.timeOffApproved + segments.timeOffPending;
  const workTotal =
    segments.trackedRegularUnder +
    segments.trackedBackgroundUnder +
    segments.trackedRegularOver +
    segments.trackedBackgroundOver +
    segments.todoUnder +
    segments.todoOver +
    segments.available;

  const showCapLine = segments.showCapLine;
  const capLineInTimeOff = segments.capLineZone === CapLineZone.TimeOff;
  // Position of the cap line inside whichever zone hosts it.
  const capLinePosInZone = segments.capLinePos;

  const items: LegendItem[] = [];
  // 1. time-off approved
  if (segments.timeOffApproved > 0) {
    items.push({
      key: LegendKey.TimeOffApproved,
      label: t('summary.legend.timeOffApproved'),
      hours: segments.timeOffApproved,
      swatch: { backgroundColor: SUMMARY_COLOR_TIMEOFF },
      explain: t('summary.legend.timeOffApprovedExplain'),
    });
  }
  // 2. time-off pending
  if (segments.timeOffPending > 0) {
    items.push({
      key: LegendKey.TimeOffPending,
      label: t('summary.legend.timeOffPending'),
      hours: segments.timeOffPending,
      swatch: { backgroundImage: swatchStripedBg(SUMMARY_COLOR_TIMEOFF) },
      explain: t('summary.legend.timeOffPendingExplain'),
    });
  }
  // 3. Tracked — hidden when there's nothing tracked AND nothing left to
  // track (e.g. on a full-time-off day) so the legend doesn't show a 0h chip.
  const trackedRegularHours = tracked - trackedBackgroundTotal;
  if (trackedRegularHours > 0 || (tracked === 0 && booked > 0)) {
    items.push({
      key: LegendKey.Tracked,
      label: t('summary.legend.tracked'),
      hours: trackedRegularHours,
      swatch: { backgroundColor: trackedColor },
      explain: t('summary.legend.trackedExplain'),
    });
  }
  // 4. Tracked · background
  if (trackedBackgroundTotal > 0) {
    items.push({
      key: LegendKey.TrackedBackground,
      label: t('summary.legend.trackedBackground'),
      hours: trackedBackgroundTotal,
      swatch: { backgroundColor: trackedColor, opacity: BACKGROUND_WORK_OPACITY },
      explain: t('summary.legend.trackedBackgroundExplain'),
    });
  }
  // 5. Left to track
  if (todoTotal > 0) {
    items.push({
      key: LegendKey.Todo,
      label: t('summary.legend.leftToLog'),
      hours: todo,
      swatch: { backgroundImage: swatchStripedBg(trackedColor) },
      explain: t('summary.legend.leftToLogExplain', {
        booked: formatHoursToTimeString(booked),
        todo: formatHoursToTimeString(todo),
      }),
    });
  }
  // 6. Available
  if (segments.available > 0) {
    items.push({
      key: LegendKey.Available,
      label: t('summary.legend.available'),
      hours: segments.available,
      swatch: {
        backgroundColor: SUMMARY_COLOR_AVAILABLE,
        border: '1px solid hsl(var(--border))',
      },
      explain: t('summary.legend.availableExplain'),
    });
  }
  // 7. Over available time
  if (segments.over > 0) {
    items.push({
      key: LegendKey.Over,
      label: t('summary.legend.overCapacity'),
      hours: segments.over,
      swatch: { backgroundColor: SUMMARY_COLOR_OVER },
      isOver: true,
      isPlus: true,
      explain: t('summary.legend.overCapacityExplain'),
    });
  }

  // Hover state — either legend or segment hover normalizes to a legend key.
  const [hoveredLegend, setHoveredLegend] = useState<LegendKey | null>(null);

  const isSegmentActive = (segment: SegmentKey): boolean =>
    hoveredLegend !== null && LEGEND_TO_SEGMENTS[hoveredLegend].includes(segment);

  const isLegendActive = (key: LegendKey): boolean => hoveredLegend === key;

  const barHeight = isCompact ? BAR_HEIGHT_COMPACT_PX : BAR_HEIGHT_DEFAULT_PX;

  return (
    <div
      className={cn(
        'flex flex-col',
        !bare && 'rounded-lg border bg-card shadow-sm',
        isCompact ? 'gap-2.5 p-4' : 'gap-3 px-5 py-4'
      )}
    >
      {/* Head: eyebrow label + big tracked number */}
      <div className="flex flex-col items-start gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
          {sublabel && (
            <span className="ml-1 font-medium text-muted-foreground/70">
              {t('summary.sublabelSeparator', { sublabel })}
            </span>
          )}
        </span>
        <div className="flex flex-wrap items-baseline gap-2 tabular-nums">
          <span
            className={cn(
              'font-semibold leading-none text-foreground',
              isCompact ? 'text-xl' : 'text-3xl'
            )}
          >
            {formatHoursToTimeString(tracked)}
          </span>
          <span className="text-sm font-medium text-muted-foreground">{t('summary.tracked')}</span>
          <span className="text-sm font-medium text-muted-foreground/70">
            {t('summary.ofCapacity', { capacity: formatHoursToTimeString(effectiveCapacity) })}
          </span>
        </div>
      </div>

      {/* Bar with time-off and work zones split into separate rounded tracks.
          The cap chip sits above the work bar (positioned within work bar's
          own coordinate system) so its left% aligns with the line below. */}
      <div className="flex flex-col">
        <div
          role="img"
          aria-label={t('summary.barAriaLabel', {
            tracked: formatHoursToTimeString(tracked),
            capacity: formatHoursToTimeString(effectiveCapacity),
          })}
          className="relative flex w-full items-stretch gap-2"
          style={{
            height: `${barHeight}px`,
            marginTop: showCapLine ? `${CAP_CHIP_OFFSET_PX}px` : '0',
          }}
        >
          {/* time-off bar — separate rounded track on the left */}
          {timeOffTotal > 0 && (
            <div
              className="relative flex h-full min-w-0 rounded-full bg-track-empty"
              style={{ flexGrow: timeOffTotal, flexBasis: 0 }}
            >
              {/* Cap chip — rendered here when time-off alone overruns capacity. */}
              {showCapLine && capLineInTimeOff && (
                <span
                  className="pointer-events-none absolute z-[1] -translate-x-1/2 whitespace-nowrap rounded-sm bg-foreground px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-background shadow-sm"
                  style={{
                    left: `${capLinePosInZone}%`,
                    top: `-${CAP_CHIP_OFFSET_PX}px`,
                  }}
                >
                  {t('summary.capLineLabel', {
                    capacity: formatHoursToTimeString(effectiveCapacity),
                  })}
                </span>
              )}
              <div className="flex h-full min-w-0 flex-1 overflow-hidden rounded-full">
                {segments.timeOffApproved > 0 && (
                  <BarSegment
                    segmentKey={SegmentKey.TimeOffApproved}
                    size={segments.timeOffApproved}
                    style={{ backgroundColor: SUMMARY_COLOR_TIMEOFF }}
                    active={isSegmentActive(SegmentKey.TimeOffApproved)}
                    onEnter={() => setHoveredLegend(LegendKey.TimeOffApproved)}
                    onLeave={() => setHoveredLegend(null)}
                  />
                )}
                {segments.timeOffPending > 0 && (
                  <BarSegment
                    segmentKey={SegmentKey.TimeOffPending}
                    size={segments.timeOffPending}
                    style={{ backgroundImage: stripedBg(SUMMARY_COLOR_TIMEOFF) }}
                    active={isSegmentActive(SegmentKey.TimeOffPending)}
                    onEnter={() => setHoveredLegend(LegendKey.TimeOffPending)}
                    onLeave={() => setHoveredLegend(null)}
                  />
                )}
              </div>

              {/* Capacity divider inside the time-off bar */}
              {showCapLine && capLineInTimeOff && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute w-[2px] -translate-x-px bg-foreground"
                  style={{
                    top: `-${CAP_DIVIDER_OVERHANG_PX}px`,
                    bottom: `-${CAP_DIVIDER_OVERHANG_PX}px`,
                    left: `${capLinePosInZone}%`,
                  }}
                />
              )}
            </div>
          )}

          {/* Work bar — separate rounded track on the right */}
          <div
            className="relative flex h-full min-w-0 rounded-full bg-track-empty"
            style={{ flexGrow: workTotal, flexBasis: 0 }}
          >
            {/* Cap chip — positioned above bar at the work-bar's interior cap %. */}
            {showCapLine && !capLineInTimeOff && (
              <span
                className="pointer-events-none absolute -translate-x-1/2 whitespace-nowrap rounded-sm bg-foreground px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-background shadow-sm"
                style={{
                  left: `${capLinePosInZone}%`,
                  top: `-${CAP_CHIP_OFFSET_PX}px`,
                }}
              >
                {t('summary.capLineLabel', {
                  capacity: formatHoursToTimeString(effectiveCapacity),
                })}
              </span>
            )}
            <div className="flex h-full min-w-0 flex-1 overflow-hidden rounded-full">
              {segments.trackedRegularUnder > 0 && (
                <BarSegment
                  segmentKey={SegmentKey.TrackedRegularUnder}
                  size={segments.trackedRegularUnder}
                  style={{ backgroundColor: trackedColor }}
                  active={isSegmentActive(SegmentKey.TrackedRegularUnder)}
                  onEnter={() => setHoveredLegend(LegendKey.Tracked)}
                  onLeave={() => setHoveredLegend(null)}
                />
              )}
              {segments.trackedBackgroundUnder > 0 && (
                <BarSegment
                  segmentKey={SegmentKey.TrackedBackgroundUnder}
                  size={segments.trackedBackgroundUnder}
                  style={{
                    backgroundColor: trackedColor,
                    opacity: BACKGROUND_WORK_OPACITY,
                    boxShadow:
                      segments.trackedRegularUnder > 0
                        ? 'inset 1px 0 0 0 rgba(0,0,0,0.25)'
                        : undefined,
                  }}
                  active={isSegmentActive(SegmentKey.TrackedBackgroundUnder)}
                  onEnter={() => setHoveredLegend(LegendKey.TrackedBackground)}
                  onLeave={() => setHoveredLegend(null)}
                />
              )}
              {segments.todoUnder > 0 && (
                <BarSegment
                  segmentKey={SegmentKey.TodoUnder}
                  size={segments.todoUnder}
                  style={{ backgroundImage: stripedBg(trackedColor) }}
                  active={isSegmentActive(SegmentKey.TodoUnder)}
                  onEnter={() => setHoveredLegend(LegendKey.Todo)}
                  onLeave={() => setHoveredLegend(null)}
                />
              )}
              {segments.available > 0 && (
                <BarSegment
                  segmentKey={SegmentKey.Available}
                  size={segments.available}
                  style={{}}
                  active={isSegmentActive(SegmentKey.Available)}
                  onEnter={() => setHoveredLegend(LegendKey.Available)}
                  onLeave={() => setHoveredLegend(null)}
                />
              )}
              {segments.trackedRegularOver > 0 && (
                <BarSegment
                  segmentKey={SegmentKey.TrackedRegularOver}
                  size={segments.trackedRegularOver}
                  style={{ backgroundColor: SUMMARY_COLOR_OVER }}
                  active={isSegmentActive(SegmentKey.TrackedRegularOver)}
                  onEnter={() => setHoveredLegend(LegendKey.Over)}
                  onLeave={() => setHoveredLegend(null)}
                />
              )}
              {segments.trackedBackgroundOver > 0 && (
                <BarSegment
                  segmentKey={SegmentKey.TrackedBackgroundOver}
                  size={segments.trackedBackgroundOver}
                  style={{
                    backgroundColor: SUMMARY_COLOR_OVER,
                    opacity: BACKGROUND_WORK_OPACITY,
                    boxShadow:
                      segments.trackedRegularOver > 0
                        ? 'inset 1px 0 0 0 rgba(0,0,0,0.35)'
                        : undefined,
                  }}
                  active={isSegmentActive(SegmentKey.TrackedBackgroundOver)}
                  onEnter={() => setHoveredLegend(LegendKey.TrackedBackground)}
                  onLeave={() => setHoveredLegend(null)}
                />
              )}
              {segments.todoOver > 0 && (
                <BarSegment
                  segmentKey={SegmentKey.TodoOver}
                  size={segments.todoOver}
                  style={{ backgroundImage: stripedBg(SUMMARY_COLOR_OVER) }}
                  active={isSegmentActive(SegmentKey.TodoOver)}
                  onEnter={() => setHoveredLegend(LegendKey.Over)}
                  onLeave={() => setHoveredLegend(null)}
                />
              )}
            </div>

            {/* Capacity divider — sibling of inner flex so it can extend past the bar height */}
            {showCapLine && !capLineInTimeOff && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute w-[2px] -translate-x-px bg-foreground"
                style={{
                  top: `-${CAP_DIVIDER_OVERHANG_PX}px`,
                  bottom: `-${CAP_DIVIDER_OVERHANG_PX}px`,
                  left: `${capLinePosInZone}%`,
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-y-1.5 pt-1">
        {items.map((item, idx) => (
          <Fragment key={item.key}>
            {idx > 0 && <span aria-hidden="true" className="mx-3 h-3 w-px bg-border" />}
            <LegendChip
              item={item}
              capacity={effectiveCapacity}
              active={isLegendActive(item.key)}
              onEnter={() => setHoveredLegend(item.key)}
              onLeave={() => setHoveredLegend(null)}
              t={t}
            />
          </Fragment>
        ))}
      </div>
    </div>
  );
}

interface BarSegmentProps {
  segmentKey: SegmentKey;
  size: number;
  style: CSSProperties;
  active: boolean;
  onEnter: () => void;
  onLeave: () => void;
}

function BarSegment({ size, style, active, onEnter, onLeave }: BarSegmentProps) {
  return (
    <div
      role="presentation"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={cn(
        'h-full min-w-[2px] origin-center transition-[transform,filter] duration-150 ease-out',
        active && 'scale-y-110 brightness-110 drop-shadow-sm'
      )}
      style={{ flex: size, ...style }}
    />
  );
}

interface LegendChipProps {
  item: LegendItem;
  capacity: number;
  active: boolean;
  onEnter: () => void;
  onLeave: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function LegendChip({ item, capacity, active, onEnter, onLeave, t }: LegendChipProps) {
  return (
    <div
      role="presentation"
      title={item.explain}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={cn(
        'inline-flex cursor-default items-baseline gap-1.5 whitespace-nowrap rounded-md px-1 py-0.5 text-[12.5px] transition-colors duration-150 ease-out',
        active && 'bg-muted'
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block h-2.5 w-2.5 shrink-0 self-center rounded-sm border border-black/5 transition-transform duration-150 ease-out',
          active && 'scale-125'
        )}
        style={item.swatch}
      />
      <span
        className={cn(
          'font-medium',
          item.isOver ? 'text-purple-700 dark:text-purple-400' : 'text-muted-foreground'
        )}
      >
        {item.label}
      </span>
      <span className="text-muted-foreground/40">{t('summary.legendSeparator')}</span>
      <span
        className={cn(
          'tabular-nums transition-all duration-150 ease-out',
          item.isOver ? 'text-purple-700 dark:text-purple-400' : 'text-foreground',
          active ? 'text-[14px] font-bold' : 'text-[13px] font-semibold'
        )}
      >
        {item.isPlus
          ? t('summary.legendValuePositive', {
              value: formatHoursToTimeString(item.hours),
            })
          : formatHoursToTimeString(item.hours)}
      </span>
      {capacity > 0 && (
        <span className="ml-0.5 text-[11.5px] tabular-nums text-muted-foreground/70">
          {item.isPlus
            ? t('summary.legendPctPositive', {
                percentage: Math.round((item.hours / capacity) * 100),
              })
            : t('summary.legendPct', {
                percentage: Math.round((item.hours / capacity) * 100),
              })}
        </span>
      )}
    </div>
  );
}
